/**
 * sessionCache.js
 * ---------------
 * Lightweight sessionStorage wrapper used to cache API responses for the
 * duration of a browser session.  Data is cleared automatically when the
 * user closes the tab/window, so it never leaks stale data across logins.
 *
 * Usage:
 *   sessionCache.set("companies", data);
 *   const data = sessionCache.get("companies");
 *   sessionCache.remove("companies");
 *   sessionCache.clear();           // wipes ALL cache keys
 */

const CACHE_PREFIX = "wp_cache_";

const sessionCache = {
  /**
   * Read a value from the cache.
   * @param {string} key
   * @returns {any|null}  Parsed value, or null if not found / parse error.
   */
  get(key) {
    try {
      const raw = sessionStorage.getItem(`${CACHE_PREFIX}${key}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  /**
   * Write a value to the cache.
   * @param {string} key
   * @param {any}    value  Must be JSON-serialisable.
   */
  set(key, value) {
    try {
      sessionStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(value));
    } catch {
      // sessionStorage quota exceeded or unavailable – fail silently.
    }
  },

  /**
   * Remove a single cached entry.
   * @param {string} key
   */
  remove(key) {
    try {
      sessionStorage.removeItem(`${CACHE_PREFIX}${key}`);
    } catch {
      // fail silently
    }
  },

  /**
   * Remove ALL entries that were written by this utility.
   * Useful on logout.
   */
  clear() {
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith(CACHE_PREFIX))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // fail silently
    }
  },
};

export default sessionCache;
